import { MICROSOFT_LOGIN_CLIENT_ID } from '@src/services/.env.js'

const normalizeRedirectUri = (uri) => {
  try {
    const url = new URL(uri)
    let normalized = `${url.origin}${url.pathname}`
    if (normalized.endsWith('/') && normalized !== url.origin + '/') {
      normalized = normalized.slice(0, -1)
    }
    return normalized
  } catch (e) {
    return uri
  }
}

const generateCodeVerifier = () => {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export const buildMicrosoftAuthUrl = async (redirectUri, subDomain, provider) => {
  const stateData = {
    sub_domain: subDomain,
    provider: provider,
  }
  const state = btoa(JSON.stringify(stateData))
  const normalizedRedirectUri = normalizeRedirectUri(redirectUri)

  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  sessionStorage.setItem('microsoft_code_verifier', codeVerifier)
  sessionStorage.setItem('microsoft_redirect_uri', normalizedRedirectUri)
  const params = new URLSearchParams({
    client_id: MICROSOFT_LOGIN_CLIENT_ID,
    redirect_uri: normalizedRedirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    response_mode: 'query',
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    prompt: 'select_account',
  })

  const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`

  return authUrl
}

export const redirectToMicrosoftLogin = async (redirectUri, subDomain, provider) => {
  const microsoftAuthUrl = await buildMicrosoftAuthUrl(redirectUri, subDomain, provider)
  window.location.href = microsoftAuthUrl
}

export const handleMicrosoftOAuthCallback = async (searchParams) => {
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')
  const state = searchParams.get('state')

  if (error) {
    let errorMessage = `Microsoft authentication failed`

    if (error === 'unauthorized_client') {
      errorMessage =
        'Microsoft login is not configured for your account type. Please contact support or try using a different account.'
    } else if (error === 'invalid_request' && errorDescription?.includes('redirect_uri')) {
      errorMessage = 'Microsoft login configuration error. Please contact support.'
    } else if (errorDescription) {
      errorMessage = `Microsoft authentication failed: ${errorDescription}`
    } else {
      errorMessage = `Microsoft authentication failed: ${error}`
    }

    return {
      error: true,
      errorMessage,
    }
  }

  if (code && state) {
    try {
      const stateData = JSON.parse(atob(state))
      const codeVerifier = sessionStorage.getItem('microsoft_code_verifier')
      const redirectUri = sessionStorage.getItem('microsoft_redirect_uri')

      if (!codeVerifier || !redirectUri) {
        return {
          error: true,
          errorMessage: 'Missing code verifier or redirect URI. Please try logging in again.',
        }
      }

      const tokenResponse = await exchangeCodeForTokens(code, codeVerifier, redirectUri)

      sessionStorage.removeItem('microsoft_code_verifier')
      sessionStorage.removeItem('microsoft_redirect_uri')

      if (tokenResponse.error) {
        return tokenResponse
      }

      return {
        error: false,
        access_token: tokenResponse.access_token,
        id_token: tokenResponse.id_token,
        provider: stateData.provider,
        sub_domain: stateData.sub_domain,
      }
    } catch (e) {
      return {
        error: true,
        errorMessage: 'Failed to process Microsoft authentication response',
      }
    }
  }

  return null
}

const exchangeCodeForTokens = async (code, codeVerifier, redirectUri) => {
  try {
    const tokenEndpoint = 'https://login.microsoftonline.com/common/oauth2/v2.0/token'

    const params = new URLSearchParams({
      client_id: MICROSOFT_LOGIN_CLIENT_ID,
      code: code,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
      code_verifier: codeVerifier,
    })

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    })

    const data = await response.json()

    if (data.error) {
      return {
        error: true,
        errorMessage: `Token exchange failed: ${data.error_description || data.error}`,
      }
    }

    return {
      error: false,
      access_token: data.access_token,
      id_token: data.id_token,
    }
  } catch (e) {
    return {
      error: true,
      errorMessage: 'Failed to exchange authorization code for tokens',
    }
  }
}

export const isMicrosoftCallback = (searchParams) => {
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  const state = searchParams.get('state')

  if (code || (error && state)) {
    try {
      if (state) {
        const stateData = JSON.parse(atob(state))
        return stateData.provider === 'microsoft'
      }
    } catch (e) {
      return false
    }
  }

  return false
}

export const hasHandledMicrosoftCallback = () => {
  return sessionStorage.getItem('microsoftOAuthHandled') === 'true'
}

export const setMicrosoftOAuthHandled = () => {
  sessionStorage.setItem('microsoftOAuthHandled', 'true')
}

export const clearMicrosoftOAuthHandled = () => {
  sessionStorage.removeItem('microsoftOAuthHandled')
}
